import { type AxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomerCredentials } from "../ui/customerDataStore";
import { captureException } from "@sentry/react";

interface AdviceLockedErrorResponse {
  message: string;
}

const isAdviceLockedError = (error: AxiosError): boolean => {
  if (error.response?.status === 500) {
    const data = error.response?.data as AdviceLockedErrorResponse | undefined;
    return (
      data?.message === "Advice is locked, user parameters cannot be updated"
    );
  }
  return false;
};

const updateCalculationById = async ({
  credentials,
  params,
}: {
  credentials: CustomerCredentials;
  params: TGetCalculationParams;
}) => {
  const signedRequest = await signApiRequest(
    "POST",
    `${import.meta.env.VITE_API_URL}/calculation/calculation`,
    credentials,
    { "content-type": "application/json" },
    {
      fid: credentials.identityId,
      params: params,
      responseType: "CUSTOMER_ADVICE",
    },
  );

  const response = await apiClient(signedRequest);
  return response.data;
};

interface UseUpdateCalculationByIdOptions {
  onAdviceLocked?: () => void;
}

export const useUpdateCalculationById = (
  options?: UseUpdateCalculationByIdOptions,
) => {
  const queryClient = useQueryClient();

  function refreshCalculationState() {
    queryClient.refetchQueries({
      queryKey: ["get-calculation-by-id"],
    });
    queryClient.refetchQueries({
      queryKey: ["get-locked-advice-by-user-id"],
    });
  }

  const updateCalculationQuery = useMutation<
    TCalulcationResponse,
    AxiosError,
    { credentials: CustomerCredentials; params: TGetCalculationParams }
  >({
    mutationFn: ({
      credentials,
      params,
    }: {
      credentials: CustomerCredentials;
      params: TGetCalculationParams;
    }) =>
      updateCalculationById({
        credentials,
        params,
      }),
    onSuccess: () => {
      refreshCalculationState();
    },
    onError: (error) => {
      if (isAdviceLockedError(error)) {
        options?.onAdviceLocked?.();
      } else {
        captureException(error, {
          tags: {
            operation: "updateCalculation",
            errorType: error.name || "UnknownError",
          },
          extra: {
            apiEndpoint: "/calculation/calculation",
          },
          level: "error",
        });
      }
      refreshCalculationState();
    },
  });
  return updateCalculationQuery;
};

export interface Insurance {
  insuranceIdentifier: string;
  insuranceCapital: string;
  insuranceCompany: string;
  owner: {
    legalName: string;
  };
  logotype: string;
  employer?: string;
}

export interface PaymentDistribution {
  T_DC: number;
  T_NonDC: number;
  K: number;
  P: number;
  G: number;
  PPM: number | null;
  I: number | null;
  T_DC_Percentage: number | null;
  T_NonDC_Percentage: number | null;
  K_Percentage: number | null;
  P_Percentage: number | null;
  G_Percentage: number | null;
  PPM_Percentage: number | null;
  I_Percentage: number | null;
  guaranteePension: number | null;
  affectedByRisk: number | null;
}

export interface AssetBreakdown {
  buildingblockName: string;
  buildingblock: string;
  value: number;
  percentage: number;
}

export interface TotalAllocation {
  assetAllocationForTransfer: {
    eq: number;
    ir: number;
    alt: number;
  };
  assetAllocationTotalBreakdown: AssetBreakdown[];
  sumOfAssets: number;
}

export interface TransferAsset {
  name: string;
  percentage: number;
  isin?: string;
}

export interface InsuranceForTransfer {
  insuranceIdentifier?: string;
  insuranceCapital?: string;
  insuranceCompany?: string;
  owner?: {
    legalName: string;
  };
  logotype?: string;
  employer?: string;
  transferDocument?: string;
}

export interface InsurancesForTransfer {
  insuranceTransferList: InsuranceForTransfer[];
  totalTransferableCapital: number;
  capitalForTransfer: number;
  precentageOfTotal: number;
}

export interface TransferAllocation {
  assetAllocation: TransferAsset[];
  insurancesForTransfer: InsurancesForTransfer;
}

export interface PensionAmount {
  totalPension: number;
  statePensionTotal: number;
}

export interface AdviceSummary {
  advicedPensionAmount: PensionAmount;
  currentPensionAmount: PensionAmount;
  totalCurrentFees: number;
  totalAdvicedFees: number;
  pensionDifference: number;
  feeDifference: number;
}

export interface SummaryTable {
  expectedPension: number;
  expectedHighYieldPension: number;
  expectedLowYieldPension: number;
  advisedExpectedPension: number;
  advisedHighYieldPension: number;
  advisedLowYieldPension: number;
  diffExpectedPension: number;
  diffHighYieldPension: number;
  diffLowYieldPension: number;
}

export interface TCalulcationResponse {
  parameters: TGetCalculationParams;
  totalInsuranceList: Insurance[];
  paymentDistribution: PaymentDistribution;
  totalAllocation: TotalAllocation;
  transferAllocation: TransferAllocation;
  adviceSummary: AdviceSummary;
  summaryTable: SummaryTable;
}

export type TGetCalculationParams = {
  zAge: number;
  riskPreference: number;
};
